<template>
  <v-label v-if="label" class="bold"
    >{{ label }}: <span lass="pl-1" v-if="labelValue">{{ labelValue }}</span></v-label
  >

  <v-text-field
    v-if="singleLine"
    :id="id"
    :aria-describedby="id"
    :aria-label="ariaLabel"
    v-model="modelValue"
    :readonly="readOnly"
    :counter="counter"
    :maxlength="maxlength"
    persistent-counter
    single-line
    :variant="readOnly ? 'outlined' : 'filled'"
  />
  <v-textarea
    v-else
    :id="id"
    :aria-describedby="id"
    :aria-label="ariaLabel"
    v-model="modelValue"
    :readonly="readOnly"
    :counter="counter"
    :maxlength="maxlength"
    persistent-counter
    auto-grow
    rows="1"
    :variant="readOnly ? 'outlined' : 'filled'"
    @keypress="handler"
    @update:model-value="truncate"
    @blur="trim"
  />
</template>
<script>
import { computed } from "vue";
import { truncateString, encodeString } from "@/composables/util";
import { useSuperstructureStore } from "@/components/inspection/store/superStructure";
import { useSubstructureStore } from "@/components/inspection/store/substructure";
import { useCulvertStore } from "@/components/inspection/store/culvert";
import { INSPECTION_PAGES } from "@/constants/Inspections";
import { useDeckStore } from "@/components/inspection/store/deck";

export default {
  name: "StructureUnitCommentTypeTextField",
  emits: ["updateComment"],
  props: {
    id: {},
    inspectionCommentType: { default: "" },
    readOnly: { default: false },
    counter: { default: "" },
    maxlength: { maxlength: "" },
    singleLine: { default: false },
    structureUnitId: { default: null },
    label: { default: "" },
    labelValue: { default: "" },
    pageValue: { default: "" },
  },
  setup(props, context) {
    let structureValue = computed(() => {
      if (props.pageValue == INSPECTION_PAGES.SUBSTRUCTURE) {
        const substructureStore = useSubstructureStore();
        return substructureStore.pageData;
      } else if (props.pageValue == INSPECTION_PAGES.CULVERT) {
        const culvertStore = useCulvertStore();
        return culvertStore.culvertData;
      } else if (props.pageValue == INSPECTION_PAGES.SUPERSTRUCTURE) {
        const store = useSuperstructureStore();
        return store.superStructureData;
      } else if (props.pageValue == INSPECTION_PAGES.DECK) {
        const store = useDeckStore();
        return store.getDeckObject();
      } else {
        return null;
      }
    });

    function getInspCommentObjectByUnitKeyAndCommentType(commentType, structureUnitId) {
      return computed(() => {
        return structureValue?.value?.structureUnitInspComments?.find(
          (element) => element?.commentType == commentType && element?.structureUnitId == structureUnitId
        );
      });
    }

    function elseCase(commentType, notes, structureUnitId) {
      return {
        commentType: commentType,
        inspectionId: structureValue?.value?.inspectionId,
        updateDateTime: null,
        notes: notes,
        structureUnitId: structureUnitId,
        updateUserId: null,
      };
    }
    const ariaLabel = computed({
      get() {
        if (props.label == "" || !props.label) {
          return props.id + " has no label";
        } else {
          return props.label + "_" + props.id;
        }
      },
    });

    const modelValue = computed({
      get() {
        return getInspCommentObjectByUnitKeyAndCommentType(props.inspectionCommentType, props.structureUnitId)?.value
          ?.notes;
      },
      set(comment) {
        let commentToSet = getInspCommentObjectByUnitKeyAndCommentType(
          props.inspectionCommentType,
          props.structureUnitId
        );
        let commentObject = encodeString(comment);
        if (commentToSet.value) {
          commentToSet.value.notes = commentObject;
        } else {
          setComment(commentObject);
        }
        context.emit("updateComment", commentObject);
      },
    });

    const setComment = (commentObject) => {
      structureValue?.value?.structureUnitInspComments?.push(
        elseCase(props.inspectionCommentType, commentObject, props.structureUnitId)
      );
    };

    const handler = (event) => {
      if (event.which < 0x20 && event.which != 13) {
        // e.which < 0x20, then it's not a printable character like backspace etc.
        // e.which!=13 , enter key
        return; // Do nothing
      }

      if (modelValue.value?.length >= Number.parseInt(props.maxlength)) {
        event.preventDefault();
        return false;
      }
    };

    const truncate = () => {
      modelValue.value = truncateString(
        modelValue.value?.replaceAll(/\r\n|\r|\n/g, "\r\n"),
        Number.parseInt(props.maxlength)
      );
    };
    const trim = () => {
      const trimmedString = modelValue.value?.trim();
      if (modelValue.value != trimmedString) {
        modelValue.value = modelValue.value?.trim();
      }
    };
    return {
      ariaLabel,
      modelValue,
      handler,
      truncate,
      trim,
    };
  },
};
</script>
